import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { CreatePayrollRunInput } from '@accounting/shared';
import { usePayrollRun, useUpdatePayrollRun } from '../../hooks/usePayroll';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { PayrollRunForm, type PayrollFormValues } from '../../components/payroll/PayrollRunForm';
import { ApiError } from '../../lib/api';
import { formatAccountingDate } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/payroll/$id_/edit')({
  component: EditPayrollRunPage,
});

function EditPayrollRunPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { data: run, isLoading, error } = usePayrollRun(companyId, id);
  const update = useUpdatePayrollRun(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !run) return <p className="field-error">Failed to load payroll run.</p>;

  if (run.status === 'void') {
    return (
      <div>
        <Link to="/payroll/$id" params={{ id }} style={{ fontSize: 13 }}>← Payroll run</Link>
        <div className="card" style={{ padding: 20, marginTop: 8 }}>
          <p style={{ marginTop: 0 }}>This payroll run has been voided and can no longer be edited.</p>
        </div>
      </div>
    );
  }

  const initial: PayrollFormValues = {
    payDate: run.payDate,
    periodStart: run.periodStart,
    periodEnd: run.periodEnd,
    memo: run.memo ?? '',
    // Carry each stub's OWN dates through the edit. Without them every stub
    // would fall back to the run header on save, and re-post on that date —
    // which silently rewrites a correctly-dated register to a single wrong day.
    stubs: run.paystubs.map((p) => ({
      employeeName: p.employeeName,
      employeeContactId: p.employeeContactId ?? '',
      amounts: Object.fromEntries(p.components.map((c) => [c.componentType, c.amount])),
      payDate: p.payDate ?? undefined,
      periodStart: p.periodStart ?? undefined,
      periodEnd: p.periodEnd ?? undefined,
    })),
  };

  const errorMessage =
    update.error instanceof ApiError
      ? (update.error.body as { error?: string } | null)?.error ?? 'Could not update payroll run'
      : update.error?.message;

  return (
    <div>
      <Link to="/payroll/$id" params={{ id }} style={{ fontSize: 13 }}>← Payroll run</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>Edit payroll — {formatAccountingDate(run.payDate)}</h1>

      <PayrollRunForm
        companyId={companyId}
        initial={initial}
        submitLabel="Save changes"
        submitting={update.isPending}
        errorMessage={errorMessage}
        cancelTo="/payroll"
        onSubmit={(input: CreatePayrollRunInput) =>
          update.mutate({ id, input }, { onSuccess: () => void navigate({ to: '/payroll/$id', params: { id } }) })
        }
      />
    </div>
  );
}
